// POs live on each MRQ (`mrq.pos`), not as a plant-level list, so flatten
// every MRQ's POs into the plant-level `pos` list the tree renders.
// Tolerates both `pos`/`bids` (documented shape, see MCS_API_BRIEF.md) and
// singular `po`/`bid` in case the payload uses those.
export const extractPlantPos = (mrqs) => {
  if (!Array.isArray(mrqs)) return [];
  return mrqs.flatMap((mrq) => {
    const list = Array.isArray(mrq?.pos)
      ? mrq.pos
      : Array.isArray(mrq?.POs)
        ? mrq.POs
        : Array.isArray(mrq?.po)
          ? mrq.po
          : [];
    return list;
  });
};

// One bid evaluation per MRQ that actually has vendor bids: a single MRQ can
// hold many vendor bids but that still counts as one evaluation. Rendered as
// the MRQ's own number + revision, keeping a back-reference to the source MRQ
// (and its bid count) for tooltips/navigation.
export const extractPlantBidEvaluations = (mrqs) => {
  if (!Array.isArray(mrqs)) return [];
  return mrqs
    .filter((mrq) => {
      const bids = Array.isArray(mrq?.bids)
        ? mrq.bids
        : Array.isArray(mrq?.bid)
          ? mrq.bid
          : Array.isArray(mrq?.Bids)
            ? mrq.Bids
            : null;
      return Array.isArray(bids) && bids.length > 0;
    })
    .map((mrq) => {
      const bids = Array.isArray(mrq?.bids)
        ? mrq.bids
        : Array.isArray(mrq?.bid)
          ? mrq.bid
          : mrq?.Bids;
      return {
        // One tree row per MRQ, so the MRQ id is the stable row id.
        id: mrq?.id,
        mrqId: mrq?.id,
        mrqNumber: mrq?.mrqNumber,
        rev: mrq?.rev,
        revision: mrq?.revision,
        mrqRevision: mrq?.mrqRevision,
        bidCount: Array.isArray(bids) ? bids.length : 0,
        // Kept for anything that needs the vendor rows later.
        bids,
        // Keep the source MRQ around without clobbering the flat fields above.
        mrq,
      };
    });
};

// Merge derived lists into an mcs-tree payload so `plantData.pos` /
// `plantData.bids` are always populated even when the backend only embeds
// them on each MRQ. Existing top-level entries win on id conflicts.
export const normalizeMcsTreeData = (raw) => {
  const data = raw && typeof raw === "object" ? raw : {};
  const mrqs = Array.isArray(data.mrqs) ? data.mrqs : [];

  const derivedPos = extractPlantPos(mrqs);
  const existingPos = Array.isArray(data.pos)
    ? data.pos
    : Array.isArray(data.posList)
      ? data.posList
      : [];
  const seenPoIds = new Set(existingPos.map((po) => po?.id).filter(Boolean));
  const mergedPos = [
    ...existingPos,
    ...derivedPos.filter((po) => !po?.id || !seenPoIds.has(po.id)),
  ];

  const derivedBids = extractPlantBidEvaluations(mrqs);
  const existingBids = Array.isArray(data.bids)
    ? data.bids
    : Array.isArray(data.bidEvaluations)
      ? data.bidEvaluations
      : [];
  const seenBidIds = new Set(existingBids.map((b) => b?.id).filter(Boolean));
  const mergedBids = [
    ...existingBids,
    ...derivedBids.filter((b) => !b?.id || !seenBidIds.has(b.id)),
  ];

  return { ...data, mrqs, pos: mergedPos, bids: mergedBids };
};
